import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconMessage } from '@arco-design/web-react/icon';
import store, { RootState } from '@/store';
import { setHistoryMsg, setInterruptMsg } from '@/store/slices/room';
import RtcClient from '@/lib/RtcClient';
import { useScene, useDeviceState } from '@/lib/useCommon';
import { COMMAND, INTERRUPT_PRIORITY } from '@/utils/handler';
import AudioLoading from '@/components/Loading/AudioLoading';
import style from './index.module.less';

import logger from '@/utils/logger';

const THRESHOLD_VOLUME = 25;
const TEXT_INPUT_DELAY_MS = 200; // 固定延迟，确保打断后服务端就绪

function TextInputController(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className, ...rest } = props;
  const dispatch = useDispatch();
  const room = useSelector((state: RootState) => state.room);
  const { localUser, msgHistory } = room;
  const { userId } = localUser;
  const volume = room.localUser.audioPropertiesInfo?.linearVolume || 0;
  const { isAudioPublished } = useDeviceState();
  const { botName } = useScene();
  const [inputValue, setInputValue] = useState('');

  const isUserSpeaking = volume >= THRESHOLD_VOLUME && isAudioPublished;

  const lastMessage = msgHistory.at(-1);
  const userRealtimeText =
    lastMessage && lastMessage.user === userId && !lastMessage.definite ? lastMessage.value : null;

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    try {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault();
        const text = inputValue.trim();

        // 若 AI 正在说话，先发送打断指令并本地标记
        if (room.isAITalking) {
          try {
            await RtcClient.commandAgent({
              agentName: botName,
              command: COMMAND.INTERRUPT,
            });
          } catch (err) {
            logger.warn('[RoomTextInput] Send INTERRUPT failed', err);
          }
          dispatch(setInterruptMsg());
        }

        // 等待 AI 停止说话，至多 500ms
        const waitAIStop = () =>
          new Promise<void>((resolve) => {
            if (!store.getState().room.isAITalking) {
              resolve();
            }

            let unsubscribe: () => void = () => {};
            const timeout = setTimeout(() => {
              unsubscribe();
              resolve();
            }, 500);

            unsubscribe = store.subscribe(() => {
              if (!store.getState().room.isAITalking) {
                clearTimeout(timeout);
                unsubscribe();
                resolve();
              }
            });
          });

        await waitAIStop();

        // 固定延迟，避免残留字幕
        await new Promise<void>((resolveDelay) => {
          setTimeout(resolveDelay, TEXT_INPUT_DELAY_MS);
        });

        dispatch(setHistoryMsg({ text, user: userId!, paragraph: true }));

        await RtcClient.commandAgent({
          command: COMMAND.EXTERNAL_TEXT_TO_LLM,
          agentName: botName,
          message: text,
          interruptMode: INTERRUPT_PRIORITY.HIGH,
        });

        setInputValue('');
      }
    } catch (error) {
      logger.error('[TextInputController] handleKeyDown error:', error);
    }
  };

  const renderContent = () => {
    if (isUserSpeaking) {
      return (
        <>
          <span className={style.inputText}>{userRealtimeText || '正在聆听...'}</span>
          <AudioLoading />
        </>
      );
    }
    return (
      <input
        type="text"
        className={style.textInput}
        placeholder="输入文字，按 Enter 发送，或直接说话"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    );
  };

  return (
    <div className={`${style.inputDisplayBox} ${className}`} {...rest}>
      <IconMessage className={style.inputIcon} />
      {renderContent()}
    </div>
  );
}

export default TextInputController;
